import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const grasseEn: ChauffeurPage = {
  slug: "grasse",
  title: "VTC & private chauffeur in Grasse",
  subtitle:
    "Private chauffeur in Grasse: Nice airport transfers, the Fragonard, Molinard and Galimard perfume houses, the old town and the hinterland, 7 days a week.",
  keywords: [
    "vtc grasse",
    "private chauffeur grasse",
    "grasse airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Grasse",
    subtitle:
      "Nice airport transfers, perfume houses, the old town and the hinterland — available 24/7.",
    keywords: [
      "VTC Grasse",
      "perfume houses chauffeur",
      "Nice airport transfer",
      "perfume capital",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Grasse",
    badges: ["Available 24/7", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Grasse",
    items: [
      {
        title: "Nice airport, door to door",
        description:
          "Nice airport around 50 minutes away via the A8, with flight tracking and a personal welcome.",
      },
      {
        title: "Perfume houses & visits",
        description:
          "Fragonard, Molinard and Galimard: drop-off at the perfume houses, waiting during the tour and the workshop.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "French/English, punctual and discreet, at ease in the old town and on the hinterland roads.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Grasse",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up with flight tracking to and from Nice Côte d’Azur airport and the stations.",
      },
      {
        title: "Perfume houses tour",
        text: "Fragonard, Molinard, Galimard and the International Perfume Museum, with waiting during the visits.",
      },
      {
        title: "Old town & culture",
        text: "Notre-Dame-du-Puy cathedral, the Fragonard villa-museum and historic lanes, with drop-off as close as possible.",
      },
      {
        title: "Hinterland & flower fields",
        text: "Jasmine and May-rose fields, Gourdon and the Gorges du Loup, to suit the light.",
      },
      {
        title: "Hire & long distance",
        text: "By the hour, the day or any distance: Cannes, Nice, Monaco and Italy.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Fragonard perfumery",
        description:
          "Historic perfume house and factory, in the heart of Grasse.",
      },
      {
        name: "Molinard & Galimard",
        description: "Perfume houses and fragrance-creation workshops.",
      },
      {
        name: "International Perfume Museum",
        description:
          "Museum devoted to the history of perfume, in the old town.",
      },
      {
        name: "Old town & cathedral",
        description: "Notre-Dame-du-Puy, historic lanes and squares.",
      },
      {
        name: "Fragonard villa-museum",
        description: "Home and museum of the painter Jean-Honoré Fragonard.",
      },
      {
        name: "Nice airport",
        description: "About 50 minutes away via the A8 motorway.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur across Grasse",
    subheading:
      "From the perfume capital to the hinterland, premium service on the hill roads.",
    paragraphs: [
      "KS Rent Services handles your journeys with a private chauffeur in Grasse and across the French Riviera: transfers from Nice airport (about 50 minutes), the perfume houses tour and long-distance trips.",
      "The world capital of perfume, Grasse rises across the hills above Cannes: our bilingual chauffeurs know Fragonard, Molinard and Galimard, the old town and the hinterland roads.",
      "Between a factory visit, a perfume-creation workshop and a trip out to Gourdon and the Gorges du Loup, your chauffeur drops you at each house and waits, with no parking worry.",
    ],
    bullets: [
      "Flight tracking & personal welcome",
      "Waiting during factory visits",
      "Hinterland roads mastered",
    ],
  },
  process: {
    heading: "Book a chauffeur in Grasse",
    steps: [
      "Share your route, timings and number of passengers.",
      "We confirm the right vehicle and the fixed fare.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Live adjustments for traffic and any last-minute changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Grasse"),
    {
      q: "How long is the transfer to Nice airport from Grasse?",
      a: "Around 50 minutes via the A8 motorway in normal conditions. We run transfers 24/7 with flight tracking and a personal welcome.",
    },
    {
      q: "Do you take visitors to the Grasse perfume houses?",
      a: "Yes, Fragonard, Molinard, Galimard and the International Perfume Museum, with drop-off at each house and waiting during the tour and the workshop.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to international guests.",
    },
    {
      q: "Which vehicles do you offer in Grasse?",
      a: "Mercedes E-Class and S-Class sedans, 7-seat van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the Jasmine Festival; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do you offer excursions into the Grasse hinterland?",
      a: "Yes, Gourdon, the Gorges du Loup and the flower fields, with a chauffeur at ease on the mountain roads.",
    },
  ],
  relatedLinks: [
    { label: "Private chauffeur in Cannes", href: "/en/chauffeur/cannes" },
    { label: "Meet and greet at Nice Airport", href: "/en/chauffeur/airport" },
    {
      label: "French Riviera rates and routes",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Car hire in Grasse", href: "/en/location/car-rental-grasse" },
  ],
  cta: {
    title: "Your private chauffeur in Grasse",
    text: "Book your transfer or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Grasse 24/7 — car service & transfers",
    description:
      "Private car service in Grasse: Nice airport transfer (~50 min), Fragonard, Molinard and Galimard perfume houses, old town and hinterland.",
    canonical: "/en/chauffeur/grasse",
  },
};

export default grasseEn;
